import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { InstallButton } from "../components/InstallButton";
import { GoogleSignInButton } from "../components/GoogleSignInButton";
import { DashboardMock, BookingsMock, CalendarMock, InvoiceMock } from "../components/ProductMockups";
import { MessagesSquare, CalendarCheck, Filter, MessageSquareText, Zap, ArrowRight, Bot, Check, X, Moon, Clock3, PoundSterling, FileText, ChevronRight } from "lucide-react";

const features = [
  { icon: MessagesSquare, title: "Answers Every Enquiry", desc: "Never miss a lead again. Ava replies instantly, 24/7, to every customer who messages your booking link — just like a real receptionist." },
  { icon: Filter, title: "Qualifies Leads", desc: "Captures name, job details, urgency and address — then scores every lead hot, warm or cold." },
  { icon: CalendarCheck, title: "Arranges Callbacks", desc: "Offers your available times and lines up a callback, so you can confirm the job — and it lands straight on your board." },
  { icon: MessageSquareText, title: "Follows Up", desc: "AI-drafted follow-ups (text, WhatsApp or email) keep warm leads from going cold." },
];

const showcase = [
  {
    mock: DashboardMock,
    tag: "Your command center",
    title: "SEE EVERY LEAD THE MOMENT IT LANDS",
    desc: "Total leads, hot enquiries, jobs booked and your conversion rate — all live on one screen. Ava captures every enquiry and scores them so you know exactly who to ring back first.",
    points: ["Real-time lead scoring (hot / warm / cold)", "Live conversion tracking", "Nothing slips through the cracks"],
  },
  {
    mock: BookingsMock,
    tag: "Bookings that book themselves",
    title: "JOBS APPROVED WITH ONE TAP",
    desc: "Ava turns a chat into a booking request with the customer's name, number, job details and address already filled in. You just set the duration and tap approve — the slot is confirmed.",
    points: ["Customer details captured automatically", "One-tap approve & confirm", "Deposits arranged directly by you"],
  },
  {
    mock: CalendarMock,
    tag: "A diary that runs itself",
    title: "YOUR WHOLE WEEK, ALWAYS UP TO DATE",
    desc: "Every confirmed job lands on your calendar. Block out days you're unavailable and Ava simply won't offer them — so you never get double-booked or called on your day off.",
    points: ["Confirmed jobs appear instantly", "Block days off in one tap", "Ava respects your working hours"],
  },
  {
    mock: InvoiceMock,
    pro: true,
    tag: "Get paid faster",
    title: "INVOICE THE JOB IN SECONDS",
    desc: "The moment a job's done, turn it into a polished invoice — your logo, tax and totals all worked out for you. Send it straight to the customer as an image over WhatsApp or email, or save it to your photos.",
    points: ["Your logo, any tax & any currency", "Totals calculated automatically", "Send by WhatsApp, email or save to photos"],
  },
];

const plans = [
  {
    id: "lite", name: "Lite", price: 45.99, tagline: "Get Ava answering and capturing every lead.",
    features: [
      { t: "250 AI credits / month", ok: true },
      { t: "Ava answering 24/7", ok: true },
      { t: "Lead capture & qualifying", ok: true },
      { t: "Shareable receptionist link", ok: true },
      { t: "Bookings & callbacks", ok: true },
      { t: "1 Ava tone", ok: true },
      { t: "AI follow-up messages", ok: false },
      { t: "Profit tracking + charts", ok: false },
      { t: "Professional invoicing", ok: false },
      { t: "Website embed widget", ok: false },
      { t: "All Ava tones", ok: false },
    ],
  },
  {
    id: "pro", name: "Pro", price: 75.99, popular: true, tagline: "The full receptionist — win back every job.",
    features: [
      { t: "500 AI credits / month", ok: true },
      { t: "Ava answering 24/7", ok: true },
      { t: "Lead capture & qualifying", ok: true },
      { t: "Shareable receptionist link", ok: true },
      { t: "Bookings & callbacks", ok: true },
      { t: "AI follow-up messages", ok: true },
      { t: "Profit tracking + charts", ok: true },
      { t: "Professional invoicing", ok: true },
      { t: "Website embed widget", ok: true },
      { t: "All Ava tones", ok: true },
    ],
  },
];

export default function Landing() {
  const navigate = useNavigate();

  const goDemo = () => navigate("/c/demo");
  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUri = window.location.origin + "/auth/google";
    const params = new URLSearchParams({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: "code",
      scope: "openid email profile",
      access_type: "online",
      include_granted_scopes: "true",
      prompt: "select_account",
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };
  const startTrial = () => {
    try { localStorage.setItem("td_signup_intent", "trial"); localStorage.removeItem("td_post_onboard"); } catch {}
    login();
  };
  const startPaid = () => {
    try { localStorage.setItem("td_signup_intent", "paid"); localStorage.setItem("td_post_onboard", "billing"); } catch {}
    login();
  };
  const signIn = () => {
    try { localStorage.setItem("td_signup_intent", "paid"); } catch {}
    login();
  };
  const seePlans = () => {
    const el = document.getElementById("pricing");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };
  const [billing, setBilling] = React.useState(() => {
    try { return localStorage.getItem("td_interval") === "year" ? "year" : "month"; } catch { return "month"; }
  });
  const chooseBilling = (v) => { setBilling(v); try { localStorage.setItem("td_interval", v); } catch {} };

  return (
    <div className="min-h-screen bg-background text-foreground grain">
      {/* Nav */}
      <nav className="flex items-center justify-between px-6 md:px-12 py-5 border-b border-white/10 bg-[#0a0a0a] sticky top-0 z-50" style={{ paddingTop: "calc(1.25rem + env(safe-area-inset-top))" }}>
        <div className="flex items-center gap-2.5" data-testid="brand-logo">
          <div className="h-9 w-9 bg-primary rounded-sm flex items-center justify-center">
            <Bot className="h-5 w-5 text-white" />
          </div>
          <span className="font-display text-2xl tracking-wide">TRADIE<span className="text-primary">DESK</span></span>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <Button data-testid="nav-demo-btn" variant="ghost" onClick={goDemo} className="rounded-sm text-foreground hover:text-primary hover:bg-secondary hidden sm:inline-flex">
            See live demo
          </Button>
          <GoogleSignInButton onClick={signIn} compact label="Sign in" testid="nav-google-signin-btn" className="hidden sm:inline-flex" />
          <Button data-testid="nav-login-btn" onClick={startTrial} className="rounded-sm bg-primary hover:bg-orange-600 text-white font-semibold">
            Start free trial
          </Button>
        </div>
      </nav>

      {/* Hero */}
      <section className="grid lg:grid-cols-2 gap-12 items-center px-6 md:px-12 py-16 md:py-24 max-w-7xl mx-auto">
        <div className="fade-up">
          <div className="inline-flex items-center gap-2 border border-primary/40 bg-primary/10 text-primary px-3 py-1.5 rounded-sm text-xs font-semibold uppercase tracking-wider mb-6">
            <Zap className="h-3.5 w-3.5" /> AI Receptionist for Trades & Service Businesses
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight mb-6">
            NEVER MISS A JOB<br />WHILE YOU'RE<br /><span className="text-primary">ON THE TOOLS.</span>
          </h1>
          <p className="text-muted-foreground text-base md:text-lg max-w-lg leading-relaxed mb-8">
            Connect Ava to your website, WhatsApp, Facebook and Instagram. She answers every message instantly, captures the customer's name, number, job and address, then drops it into one app to confirm and invoice — a full-time receptionist for a fraction of the price. Built for trades, brilliant for any service business.
          </p>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <Button data-testid="hero-login-btn" onClick={startTrial} className="rounded-sm bg-primary hover:bg-orange-600 text-white font-semibold text-base h-12 px-7">
              Start free trial <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
            <InstallButton always showLogo size="lg" label="Get the app" variant="outline" className="rounded-full h-12 px-6 text-base font-semibold border-primary/50 text-foreground bg-primary/5 hover:bg-primary/15 hover:border-primary transition-colors" />
            <Button data-testid="hero-subscribe-btn" onClick={seePlans} className="rounded-sm bg-[#0a0a0a] hover:bg-black text-white font-semibold text-base h-12 px-7 border border-white/15">
              See prices
            </Button>
            <Button data-testid="hero-demo-btn" onClick={goDemo} variant="ghost" className="rounded-sm text-foreground hover:text-primary hover:bg-secondary h-12 px-5">
              See Ava in action
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <span className="text-sm text-muted-foreground">Already a member?</span>
            <GoogleSignInButton onClick={signIn} compact label="Sign in" testid="hero-google-signin-btn" />
          </div>
          <p className="text-xs text-muted-foreground mb-10">Free trial only when you choose it · Subscribe anytime · Live in 2 minutes</p>
          <div className="flex flex-wrap gap-2">
            <span className="text-xs uppercase tracking-wide text-muted-foreground border border-white/10 px-3 py-1.5 rounded-sm">Any trade</span>
            <span className="text-xs uppercase tracking-wide text-muted-foreground border border-white/10 px-3 py-1.5 rounded-sm">Any service business</span>
            <span className="text-xs uppercase tracking-wide text-muted-foreground border border-white/10 px-3 py-1.5 rounded-sm">Ava adapts to you</span>
          </div>
        </div>

        <div className="relative fade-up" style={{ animationDelay: "0.15s" }}>
          <div className="absolute -inset-4 bg-primary/20 blur-3xl rounded-full" />
          <div className="relative"><InvoiceMock /></div>
        </div>
      </section>

      {/* The full journey strip */}
      <section className="px-6 md:px-12 pt-2 pb-10 max-w-7xl mx-auto" data-testid="journey-strip">
        <p className="text-center text-xs uppercase tracking-[0.2em] text-muted-foreground mb-5">One tool, from first message to money in the bank</p>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-2">
          {[
            { icon: MessagesSquare, label: "Enquiry", sub: "Ava replies instantly" },
            { icon: CalendarCheck, label: "Booked", sub: "Job in your diary" },
            { icon: FileText, label: "Invoiced", sub: "Sent as an image", pro: true },
            { icon: PoundSterling, label: "Paid", sub: "Money in the bank" },
          ].map((s, i, arr) => (
            <React.Fragment key={s.label}>
              <div className="flex-1 max-w-[220px] mx-auto sm:mx-0 bg-[#141416] border border-white/10 rounded-sm px-4 py-4 flex items-center gap-3 relative">
                <span className="h-10 w-10 rounded-sm bg-primary/15 flex items-center justify-center shrink-0"><s.icon className="h-5 w-5 text-primary" /></span>
                <div className="min-w-0">
                  <p className="font-display text-lg tracking-wide leading-none flex items-center gap-1.5">{s.label}{s.pro && <span className="text-[9px] font-bold uppercase tracking-wider bg-primary text-white px-1.5 py-0.5 rounded">Pro</span>}</p>
                  <p className="text-xs text-muted-foreground mt-1">{s.sub}</p>
                </div>
              </div>
              {i < arr.length - 1 && <ChevronRight className="hidden sm:block h-5 w-5 text-muted-foreground/50 shrink-0" />}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* The maths — cost comparison */}
      <section className="px-6 md:px-12 py-16 max-w-7xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <h2 className="font-display text-4xl md:text-6xl tracking-tight">THE MATHS IS SIMPLE</h2>
          <p className="text-muted-foreground mt-3 text-base md:text-lg">A receptionist clocks off at 5pm and costs a fortune. Ava never sleeps — and costs less than a day of their wages.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-5 items-stretch">
          {/* Human receptionist */}
          <div className="bg-[#141416] border border-white/10 rounded-sm p-8 fade-up" data-testid="compare-human">
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3">A human receptionist</p>
            <div className="flex items-end gap-2 mb-1">
              <span className="font-display text-5xl md:text-6xl tracking-tight text-muted-foreground">£18,000</span>
              <span className="text-muted-foreground mb-2">/ year minimum</span>
            </div>
            <p className="text-sm text-muted-foreground mb-6">…plus National Insurance, pension, holiday & sick pay.</p>
            <ul className="space-y-3">
              {["Clocks off at 5pm — misses evening & weekend jobs", "Sick days, holidays and lunch breaks", "One call at a time", "Needs training, a desk and a wage every month"].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                  <span className="h-5 w-5 rounded-sm bg-white/5 flex items-center justify-center shrink-0 mt-0.5"><X className="h-3.5 w-3.5 text-red-400" /></span>{t}
                </li>
              ))}
            </ul>
          </div>
          {/* TradieDesk */}
          <div className="bg-primary rounded-sm p-8 fade-up relative overflow-hidden" style={{ animationDelay: "0.1s" }} data-testid="compare-tradiedesk">
            <div className="absolute top-4 right-4 bg-black/20 text-white text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm">Best value</div>
            <p className="text-xs uppercase tracking-wider text-white/80 mb-3">TradieDesk — Ava</p>
            <div className="flex items-end gap-2 mb-1">
              <span className="font-display text-5xl md:text-6xl tracking-tight text-white">£45.99</span>
              <span className="text-white/80 mb-2">/ month</span>
            </div>
            <p className="text-sm text-white/80 mb-6">That's just £1.53 a day — one coffee covers two whole days of Ava. Cancel anytime.</p>
            <ul className="space-y-3">
              {["Works 24/7/365 — books jobs while you sleep", "Never sick, never on holiday, never late", "Answers unlimited enquiries at once", "Live in 2 minutes — no training, no desk, no drama"].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-sm text-white">
                  <span className="h-5 w-5 rounded-sm bg-white/20 flex items-center justify-center shrink-0 mt-0.5"><Check className="h-3.5 w-3.5 text-white" /></span>{t}
                </li>
              ))}
            </ul>
            <div className="flex flex-col sm:flex-row gap-3 mt-7">
              <Button data-testid="compare-subscribe-btn" onClick={startPaid} className="rounded-sm bg-[#0a0a0a] hover:bg-black text-white font-semibold h-12 px-7 w-full sm:w-auto">
                Subscribe now — from £45.99/mo <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
              <Button data-testid="compare-cta-btn" onClick={startTrial} variant="outline" className="rounded-sm border-black/30 bg-transparent text-black hover:bg-black/10 font-semibold h-12 px-7 w-full sm:w-auto">
                Or start free trial
              </Button>
            </div>
          </div>
        </div>
        {/* quick stat strip */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-5">
          {[
            { icon: Clock3, big: "24/7", small: "Always answering, never off the clock" },
            { icon: Moon, big: "3am", small: "Books the emergency call-out you'd have missed" },
            { icon: PoundSterling, big: "£1.53", small: "A day on Lite — less than half a coffee" },
          ].map((s) => (
            <div key={s.big} className="bg-[#141416] border border-white/10 rounded-sm p-5 flex items-center gap-4">
              <div className="h-11 w-11 bg-primary/15 rounded-sm flex items-center justify-center shrink-0"><s.icon className="h-5 w-5 text-primary" /></div>
              <div><span className="font-display text-2xl tracking-tight">{s.big}</span><p className="text-xs text-muted-foreground">{s.small}</p></div>
            </div>
          ))}
        </div>
      </section>

      {/* Plans */}
      <section className="px-6 md:px-12 py-16 max-w-7xl mx-auto" id="pricing">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <h2 className="font-display text-4xl md:text-6xl tracking-tight">CHOOSE YOUR PLAN</h2>
          <p className="text-muted-foreground mt-3 text-base md:text-lg">Start on Lite and get Ava answering, or go Pro for the full receptionist. Win back a single job and Ava's already paid for herself.</p>
        </div>
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center bg-[#18181b] border border-white/10 rounded-full p-1" data-testid="landing-billing-toggle">
            <button onClick={() => chooseBilling("month")} data-testid="landing-billing-monthly" className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${billing === "month" ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}>Monthly</button>
            <button onClick={() => chooseBilling("year")} data-testid="landing-billing-yearly" className={`px-5 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${billing === "year" ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}>Yearly <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full ${billing === "year" ? "bg-white/20 text-white" : "bg-primary/15 text-primary"}`}>1 month free</span></button>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto items-stretch">
          {plans.map((plan, i) => (
            <div
              key={plan.id}
              data-testid={`landing-plan-${plan.id}`}
              className={`rounded-sm p-8 relative flex flex-col fade-up ${plan.popular ? "bg-[#18181b] border-2 border-primary" : "bg-[#141416] border border-white/10"}`}
              style={{ animationDelay: `${i * 0.1}s` }}
            >
              {plan.popular && <div className="absolute top-4 right-4 bg-primary text-white text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm">Most popular</div>}
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">TradieDesk {plan.name}</p>
              <div className="flex items-end gap-1.5 mb-1">
                <span className={`font-display text-5xl tracking-tight ${plan.popular ? "text-primary" : "text-foreground"}`}>£{billing === "year" ? (plan.price * 11).toFixed(2) : plan.price}</span>
                <span className="text-muted-foreground mb-2">/ {billing === "year" ? "year" : "month"}</span>
              </div>
              {billing === "year"
                ? <p className="text-xs text-primary mb-6" data-testid={`landing-annual-note-${plan.id}`}>£{plan.price}/mo billed annually · 1 month free</p>
                : <>
                    <p className="text-sm text-muted-foreground mb-2">{plan.tagline}</p>
                    <button onClick={() => chooseBilling("year")} data-testid={`landing-annual-nudge-${plan.id}`} className="text-xs text-primary hover:underline text-left mb-6 block">Pay yearly & get a month free — save £{plan.price} →</button>
                  </>}
              <ul className="space-y-2.5 mb-7 flex-1">
                {plan.features.map((f) => (
                  <li key={f.t} className={`flex items-start gap-2.5 text-sm ${f.ok ? "" : "text-muted-foreground/50"}`}>
                    <span className={`h-5 w-5 rounded-sm flex items-center justify-center shrink-0 mt-0.5 ${f.ok ? "bg-primary/15" : "bg-white/5"}`}>
                      {f.ok ? <Check className="h-3.5 w-3.5 text-primary" /> : <X className="h-3.5 w-3.5 text-muted-foreground/60" />}
                    </span>
                    {f.t}
                  </li>
                ))}
              </ul>
              <Button
                data-testid={`landing-plan-${plan.id}-btn`}
                onClick={startPaid}
                className={`w-full rounded-sm h-12 text-base font-semibold ${plan.popular ? "bg-primary hover:bg-orange-600 text-white" : "bg-[#0a0a0a] hover:bg-black text-white border border-white/15"}`}
              >
                Choose {plan.name} — {billing === "year" ? `£${(plan.price * 11).toFixed(2)}/yr` : `£${plan.price}/mo`}
              </Button>
            </div>
          ))}
        </div>
        <p className="text-center text-xs text-muted-foreground mt-6">All prices in GBP · Monthly or yearly (1 month free) · Cancel anytime · Or <button onClick={startTrial} data-testid="landing-plan-trial-btn" className="text-primary underline underline-offset-2 hover:text-orange-500">start a free trial</button></p>
      </section>

      {/* Product showcase (zig-zag) */}
      <section className="px-6 md:px-12 py-8 max-w-7xl mx-auto">
        <div className="max-w-2xl mb-12">
          <h2 className="font-display text-4xl md:text-5xl tracking-tight mb-3">THIS IS WHAT YOU GET</h2>
          <p className="text-muted-foreground text-base md:text-lg">A proper dashboard built for trades — not a clunky spreadsheet. Here's exactly what lands on your screen.</p>
        </div>
        <div className="space-y-16 md:space-y-24">
          {showcase.map((s, i) => {
            const Mock = s.mock;
            const flip = i % 2 === 1;
            return (
              <div key={s.title} className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center fade-up" data-testid={`showcase-${i}`}>
                <div className={`relative ${flip ? "lg:order-2" : ""}`}>
                  <div className="absolute -inset-4 bg-primary/10 blur-3xl rounded-full" />
                  <div className="relative"><Mock /></div>
                </div>
                <div className={flip ? "lg:order-1" : ""}>
                  <div className="flex items-center gap-2">
                    <span className="text-xs uppercase tracking-wider text-primary font-semibold">{s.tag}</span>
                    {s.pro && <span className="text-[10px] font-bold uppercase tracking-wider bg-primary text-white px-2 py-0.5 rounded-sm" data-testid={`showcase-pro-badge-${i}`}>Pro feature</span>}
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl tracking-tight mt-2 mb-4">{s.title}</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">{s.desc}</p>
                  <ul className="space-y-2.5">
                    {s.points.map((p) => (
                      <li key={p} className="flex items-center gap-2.5 text-sm">
                        <span className="h-5 w-5 rounded-sm bg-primary/15 flex items-center justify-center shrink-0"><Check className="h-3.5 w-3.5 text-primary" /></span>{p}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Features */}
      <section className="px-6 md:px-12 py-16 max-w-7xl mx-auto">
        <h2 className="font-display text-4xl md:text-5xl tracking-tight mb-3">FROM ENQUIRY TO BOOKED JOB.</h2>
        <p className="text-muted-foreground mb-12 max-w-2xl">Everything a front-desk receptionist does — automated, instant and always on.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {features.map((f, i) => (
            <div key={f.title} className="bg-[#18181b] border border-white/10 rounded-sm p-6 hover:border-primary/50 transition-[border-color,transform] duration-200 hover:-translate-y-1 fade-up" style={{ animationDelay: `${i * 0.08}s` }} data-testid={`feature-${i}`}>
              <div className="h-11 w-11 bg-primary/15 rounded-sm flex items-center justify-center mb-4">
                <f.icon className="h-5 w-5 text-primary" />
              </div>
              <h3 className="font-display text-xl tracking-wide mb-2">{f.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-12 py-16 max-w-7xl mx-auto">
        <div className="bg-primary rounded-sm p-10 md:p-14 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight text-white">READY TO STOP LOSING JOBS?</h2>
            <p className="text-white/90 mt-2">Subscribe from £45.99/mo, start a free trial, or preview the AI receptionist your customers will use.</p>
          </div>
          <div className="flex flex-wrap gap-3 shrink-0">
            <Button data-testid="cta-subscribe-btn" onClick={startPaid} className="rounded-sm bg-[#0a0a0a] hover:bg-black text-white font-semibold h-12 px-8">
              Subscribe now <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
            <Button data-testid="cta-login-btn" onClick={startTrial} variant="outline" className="rounded-sm border-black/30 bg-transparent text-black hover:bg-black/10 font-semibold h-12 px-8">
              Start free trial
            </Button>
            <Button data-testid="cta-demo-btn" onClick={goDemo} variant="outline" className="rounded-sm border-black/30 bg-transparent text-black hover:bg-black/10 font-semibold h-12 px-8">
              See live demo
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 px-6 md:px-12 py-8 text-center text-sm text-muted-foreground">
        <div>TradieDesk · AI Receptionist for Trades</div>
        <div className="flex items-center justify-center gap-4 mt-3">
          <a href="/terms" data-testid="footer-terms-link" className="hover:text-primary transition-colors">Terms</a>
          <span className="opacity-30">·</span>
          <a href="/privacy" data-testid="footer-privacy-link" className="hover:text-primary transition-colors">Privacy</a>
          <span className="opacity-30">·</span>
          <a href="/data-deletion" data-testid="footer-data-deletion-link" className="hover:text-primary transition-colors">Data deletion</a>
        </div>
      </footer>
    </div>
  );
}
